/**
 * Respostas de um formulario — /comercial/formularios/:id/respostas.
 *
 * Lista paginada e filtrada das submissoes (FORM-26): o servidor pagina,
 * filtra (origem, lead, integracao, periodo, busca) e os KPIs vem de
 * /metricas com o MESMO filtro da tabela. Clicar numa linha abre um
 * SlideOverPanel com todas as respostas daquela submissao, inclusive os
 * campos que ja sairam do formulario (FORM-17, `campos_snapshot`).
 *
 * Resposta que o CRM recusou fica FALHOU (FORM-13) e ganha "Reprocessar";
 * "Excluir resposta" (FORM-27) exige formulario:manage e MFA recente — o
 * step-up e' transparente pelo interceptor do AuthContext.
 *
 * Sem scroll lateral: a tabela usa colunas fixas e ellipsis.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FiArrowRight, FiEye, FiInbox, FiRefreshCw, FiTrash2 } from "react-icons/fi";

import AsyncButton from "../../components/AsyncButton";
import ConfirmDialog from "../../components/ConfirmDialog";
import DocumentoGrid from "../../components/DocumentoGrid";
import EntityHeader from "../../components/EntityHeader";
import ExportMenu from "../../components/ExportMenu";
import KpiStrip from "../../components/KpiStrip";
import SlideOverPanel from "../../components/SlideOverPanel";
import { PagBotoes, PagBtn, PagInfo, Paginacao } from "../../components/PainelTabela/styles";
import { useAuth } from "../../context/AuthContext";
import { useToast } from "../../context/ToastContext";
import { useExportSnapshot } from "../../hooks/useExportSnapshot";
import {
  INTEGRACAO_FALHOU,
  INTEGRACAO_OK,
  RESPOSTAS_POR_PAGINA_PADRAO,
  TIPO_CAMPO_LABEL,
  anexosDoValorDeArquivo,
  excluirResposta,
  listarRespostas,
  listarTodasRespostas,
  obterFormulario,
  obterMetricasFormulario,
  reprocessarResposta,
  tipoSemInput,
} from "../../services/formularios";
import { formatarPercentual } from "../../utils/dinheiro";
import { anexoParaDocumento } from "../../utils/documentos";
import { salvarCSV, salvarExcel, salvarPDF } from "../../utils/exportacao";
import { PageContainer, PageState } from "../../styles/shared";

import FormTabs from "./FormTabs";
import { FilterBar, Pill, SearchInput, Select } from "../Orcamentos/styles";
import { PAINEL_EXPORTACAO_RESPOSTAS, montarExportacaoRespostas } from "./respostasExport";
import {
  camposRemovidosDaResposta,
  colunasDeCampos,
  formatarRecebidaEm,
  formatarValorResposta,
  rotuloEfeitoLead,
  rotuloOrigem,
  rotuloStatusIntegracao,
  textoCruResposta,
  tomOrigem,
  tomStatusIntegracao,
} from "./respostasFormat";
import { RespostaDetalheList } from "./styles";

// Colunas dinamicas na tabela: mais que isso gera scroll lateral.
const MAXIMO_DE_COLUNAS_NA_TABELA = 4;

const FILTROS_INICIAIS = {
  origem: "",
  com_lead: "",
  status_integracao: "",
  de: "",
  ate: "",
  busca: "",
  ordering: "-created_at",
};

const EXPORTADORES = { pdf: salvarPDF, excel: salvarExcel, csv: salvarCSV };

const ROTULO_DO_FILTRO = {
  origem: (v) => `Origem: ${rotuloOrigem(v)}`,
  com_lead: (v) => (v === "true" ? "Com lead" : "Sem lead"),
  status_integracao: (v) => `Integração: ${rotuloStatusIntegracao(v)}`,
  de: (v) => `De ${v}`,
  ate: (v) => `Até ${v}`,
  busca: (v) => `Busca: ${v}`,
};

/** Filtros ativos como texto, para a capa do PDF e o estado vazio. */
function descreverFiltros(filtros) {
  return Object.entries(ROTULO_DO_FILTRO)
    .filter(([chave]) => filtros[chave])
    .map(([chave, rotulo]) => rotulo(filtros[chave]));
}

/** Anexos gravados nos campos ARQUIVO da resposta -> documentos do DocumentoGrid. */
function documentosDaResposta(resposta) {
  const valores = Object.values(resposta.dados || {});
  return valores
    .flatMap((valor) => anexosDoValorDeArquivo(valor))
    .map((anexo) =>
      anexoParaDocumento(
        {
          id: anexo.anexo_id,
          nome_original: anexo.nome,
          download_url: `/api/crm/anexos/${anexo.anexo_id}/download`,
          mime_type: "",
          tamanho_bytes: anexo.tamanho_bytes || 0,
          created_at: resposta.created_at,
          descricao: "",
        },
        { fonte: "Formulário" },
      ),
    );
}

export default function Respostas() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { request, hasAllPermissions } = useAuth();
  const toast = useToast();
  // Reprocessar e excluir exigem formulario:manage no backend; quem so ve
  // respostas nao recebe os botoes (FORM-43).
  const podeGerir = hasAllPermissions(["formulario:manage"]);

  const [form, setForm] = useState(null);
  const [metricas, setMetricas] = useState(null);
  const [respostas, setRespostas] = useState([]);
  const [total, setTotal] = useState(0);
  const [pagina, setPagina] = useState(1);
  const [filtros, setFiltros] = useState(FILTROS_INICIAIS);
  // A busca so vai ao servidor no Enter/blur: tecla por tecla seria uma
  // request por letra.
  const [buscaDigitada, setBuscaDigitada] = useState("");
  const [loading, setLoading] = useState(true);
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [erro, setErro] = useState(null);

  // Submissao aberta no painel de detalhe (ou null).
  const [detalhe, setDetalhe] = useState(null);
  const [confirmandoExclusao, setConfirmandoExclusao] = useState(false);
  const pageRef = useRef(null);

  useEffect(() => {
    let vivo = true;
    obterFormulario(request, id)
      .then((detalheForm) => {
        if (vivo) setForm(detalheForm);
      })
      .catch((err) => {
        if (vivo) setErro(err?.message || "Erro ao carregar o formulário.");
      });
    return () => {
      vivo = false;
    };
  }, [request, id]);

  const carregarLista = useCallback(async () => {
    setCarregandoLista(true);
    setErro(null);
    try {
      const [lista, m] = await Promise.all([
        listarRespostas(request, id, filtros, {
          pagina, porPagina: RESPOSTAS_POR_PAGINA_PADRAO,
        }),
        obterMetricasFormulario(request, id, filtros),
      ]);
      setRespostas(Array.isArray(lista?.resultados) ? lista.resultados : []);
      setTotal(Number(lista?.total) || 0);
      setMetricas(m);
    } catch (err) {
      setErro(err?.message || "Erro ao carregar respostas.");
    } finally {
      setCarregandoLista(false);
      setLoading(false);
    }
  }, [request, id, filtros, pagina]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  const aplicarFiltro = (patch) => {
    setPagina(1);
    setFiltros((prev) => ({ ...prev, ...patch }));
  };

  const limparFiltros = () => {
    setBuscaDigitada("");
    setPagina(1);
    setFiltros(FILTROS_INICIAIS);
  };

  const temFiltro = descreverFiltros(filtros).length > 0;

  // Campos com input (SECAO fora), na ordem do formulario.
  const camposInput = useMemo(
    () => (form?.campos || []).filter((c) => !tipoSemInput(c.tipo)),
    [form]
  );

  // Atuais + removidos (FORM-17), para tabela e exportacao.
  const colunasTodas = useMemo(
    () => colunasDeCampos(camposInput, metricas?.campos_removidos || []),
    [camposInput, metricas]
  );
  const colunas = useMemo(
    () => colunasTodas.slice(0, MAXIMO_DE_COLUNAS_NA_TABELA),
    [colunasTodas]
  );
  const colunasOcultas = Math.max(colunasTodas.length - colunas.length, 0);

  const kpis = useMemo(() => {
    if (!metricas) return [];
    const origem = Object.fromEntries(
      (metricas.por_origem || []).map((o) => [o.origem, o.count])
    );
    const itens = [{ label: "Respostas", value: metricas.total_respostas ?? total }];
    // Rotulo por tipo, sem "Conversao" (FORM-24): GENERICO nao tem KPI de lead.
    if (metricas.rotulo_leads) {
      const enriquecer = metricas.rotulo_leads === "Leads enriquecidos";
      itens.push({
        label: metricas.rotulo_leads,
        value: enriquecer ? metricas.leads_enriquecidos : metricas.leads_criados,
        sub: `${formatarPercentual(metricas.percentual_leads)} de ${metricas.respostas_consideradas} consideradas`,
        title: "Fora da conta: respostas de teste e as que falharam no CRM.",
      });
    }
    itens.push({
      label: "Falharam no CRM",
      value: metricas.respostas_falhas ?? 0,
      tone: metricas.respostas_falhas > 0 ? "danger" : undefined,
    });
    itens.push({ label: "Públicas", value: origem.PUBLICO || 0 });
    itens.push({ label: "Internas", value: origem.INTERNO || 0 });
    return itens;
  }, [metricas, total]);

  const totalPaginas = Math.max(1, Math.ceil(total / RESPOSTAS_POR_PAGINA_PADRAO));
  const inicio = total === 0 ? 0 : (pagina - 1) * RESPOSTAS_POR_PAGINA_PADRAO + 1;
  const fim = Math.min(pagina * RESPOSTAS_POR_PAGINA_PADRAO, total);

  // Snapshot da pagina atual no registry de exportacao (regra 4 da
  // Definicao de Pronto).
  const exportPayload = useMemo(
    () => ({
      ...montarExportacaoRespostas({
        formulario: form, respostas, colunas: colunasTodas, filtros: descreverFiltros(filtros),
      }),
      pronto: !loading && Boolean(form),
    }),
    [form, respostas, colunasTodas, filtros, loading]
  );
  useExportSnapshot(`${PAINEL_EXPORTACAO_RESPOSTAS}-${id}`, exportPayload, pageRef);

  // Exporta TODAS as paginas do filtro, nao so a visivel.
  const exportar = async (formato) => {
    const salvar = EXPORTADORES[formato];
    if (!salvar) return;
    try {
      const todas = await listarTodasRespostas(request, id, filtros);
      salvar(
        montarExportacaoRespostas({
          formulario: form, respostas: todas, colunas: colunasTodas, filtros: descreverFiltros(filtros),
        })
      );
    } catch (err) {
      toast.error(err?.message || "Não foi possível exportar as respostas.");
    }
  };

  const substituirResposta = (atualizada) => {
    setRespostas((prev) => prev.map((r) => (r.id === atualizada.id ? atualizada : r)));
    setDetalhe((atual) => (atual && atual.id === atualizada.id ? atualizada : atual));
  };

  const reprocessar = async () => {
    // silentError no request: o AsyncButton ja mostra o motivo do 400.
    const atualizada = await reprocessarResposta(request, id, detalhe.id, { silentError: true });
    substituirResposta(atualizada);
    if (atualizada.status_integracao === INTEGRACAO_OK) {
      toast.success("Resposta integrada ao CRM.");
    } else {
      toast.warning(atualizada.erro_integracao || "O CRM recusou a resposta de novo.");
    }
    await carregarLista();
  };

  const excluir = async () => {
    // Erro fica no toast automatico do request; o dialogo continua aberto.
    await excluirResposta(request, id, detalhe.id);
    setConfirmandoExclusao(false);
    setDetalhe(null);
    toast.success("Resposta excluída.");
    await carregarLista();
  };

  if (loading) {
    return (
      <PageContainer>
        <PageState>Carregando respostas...</PageState>
      </PageContainer>
    );
  }

  const camposRemovidosDoDetalhe = detalhe ? camposRemovidosDaResposta(detalhe, camposInput) : [];
  // Anexos reais dos campos ARQUIVO (FORM-05): abrem pelo proxy autenticado,
  // no mesmo DocumentoGrid do cockpit do lead.
  const documentosDoDetalhe = detalhe ? documentosDaResposta(detalhe) : [];

  return (
    <PageContainer ref={pageRef}>
      <EntityHeader
        onBack={() => navigate(`/comercial/formularios/${id}`)}
        backLabel="Editor"
        title={form ? `Respostas — ${form.nome}` : "Respostas"}
        subtitle={form?.slug ? `/f/${form.slug}` : undefined}
      />

      <FormTabs id={id} ativa="respostas" />

      <KpiStrip items={kpis} />

      {erro && (
        <div className="so-banner danger" role="alert">
          <span>{erro}</span>
        </div>
      )}

      <FilterBar style={{ marginBottom: 12 }} role="search" aria-label="Filtros das respostas">
        <SearchInput
          type="search"
          aria-label="Buscar por nome, e-mail ou texto da resposta"
          placeholder="Buscar por nome, e-mail ou texto..."
          value={buscaDigitada}
          onChange={(e) => setBuscaDigitada(e.target.value)}
          onBlur={() => {
            if (buscaDigitada.trim() !== filtros.busca) aplicarFiltro({ busca: buscaDigitada.trim() });
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") aplicarFiltro({ busca: buscaDigitada.trim() });
          }}
        />
        <Select
          aria-label="Origem"
          value={filtros.origem}
          onChange={(e) => aplicarFiltro({ origem: e.target.value })}
        >
          <option value="">Todas as origens</option>
          <option value="PUBLICO">{rotuloOrigem("PUBLICO")}</option>
          <option value="INTERNO">{rotuloOrigem("INTERNO")}</option>
        </Select>
        <Select
          aria-label="Lead"
          value={filtros.com_lead}
          onChange={(e) => aplicarFiltro({ com_lead: e.target.value })}
        >
          <option value="">Com e sem lead</option>
          <option value="true">Com lead</option>
          <option value="false">Sem lead</option>
        </Select>
        <Select
          aria-label="Integração com o CRM"
          value={filtros.status_integracao}
          onChange={(e) => aplicarFiltro({ status_integracao: e.target.value })}
        >
          <option value="">Integradas e falhas</option>
          <option value={INTEGRACAO_OK}>{rotuloStatusIntegracao(INTEGRACAO_OK)}</option>
          <option value={INTEGRACAO_FALHOU}>{rotuloStatusIntegracao(INTEGRACAO_FALHOU)}</option>
        </Select>
        <input
          className="input"
          type="date"
          aria-label="De"
          value={filtros.de}
          onChange={(e) => aplicarFiltro({ de: e.target.value })}
          style={{ width: "auto" }}
        />
        <input
          className="input"
          type="date"
          aria-label="Até"
          value={filtros.ate}
          onChange={(e) => aplicarFiltro({ ate: e.target.value })}
          style={{ width: "auto" }}
        />
        <Select
          aria-label="Ordenação"
          value={filtros.ordering}
          onChange={(e) => aplicarFiltro({ ordering: e.target.value })}
        >
          <option value="-created_at">Mais recentes primeiro</option>
          <option value="created_at">Mais antigas primeiro</option>
        </Select>
        {temFiltro && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={limparFiltros}>
            Limpar filtros
          </button>
        )}
        <span style={{ marginLeft: "auto" }}>
          <ExportMenu onExport={exportar} disabled={total === 0} />
        </span>
      </FilterBar>

      {total === 0 ? (
        <div className="empty">
          <div className="art">
            <FiInbox size={40} aria-hidden="true" />
          </div>
          <p className="title">{temFiltro ? "Nenhuma resposta com este filtro" : "Nenhuma resposta ainda"}</p>
          <p className="msg">
            {temFiltro
              ? "Ajuste ou limpe os filtros para ver as demais."
              : "As submissões do link público aparecerão aqui assim que chegarem."}
          </p>
        </div>
      ) : (
        <div className="table-wrap" aria-busy={carregandoLista}>
          <table className="dtable" style={{ tableLayout: "fixed" }}>
            <colgroup>
              <col style={{ width: 150 }} />
              {colunas.map((col) => (
                <col key={col.id} />
              ))}
              <col style={{ width: 120 }} />
              <col style={{ width: 120 }} />
              <col style={{ width: 110 }} />
              <col style={{ width: 56 }} />
            </colgroup>
            <thead>
              <tr>
                <th>Recebida em</th>
                {colunas.map((col) => (
                  <th key={col.id} title={col.removido ? `${col.label} (campo removido)` : col.label}>
                    {col.label}
                    {col.removido && <span className="tiny muted"> (removido)</span>}
                  </th>
                ))}
                <th>Origem</th>
                <th>CRM</th>
                <th>Lead</th>
                <th aria-label="Detalhe" />
              </tr>
            </thead>
            <tbody>
              {respostas.map((r) => (
                <tr
                  key={r.id}
                  onClick={() => setDetalhe(r)}
                  style={{ cursor: "pointer" }}
                >
                  <td className="mono" title={formatarRecebidaEm(r.created_at)}>
                    {formatarRecebidaEm(r.created_at)}
                  </td>
                  {colunas.map((col) => {
                    const bruto = r.dados?.[col.id];
                    const valor = formatarValorResposta(col.campo, bruto);
                    return (
                      <td
                        key={col.id}
                        title={textoCruResposta(bruto) || valor}
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                          maxWidth: 0,
                        }}
                      >
                        {valor}
                      </td>
                    );
                  })}
                  <td>
                    {r.origem_submissao ? (
                      <Pill $tone={tomOrigem(r.origem_submissao)}>
                        {rotuloOrigem(r.origem_submissao)}
                      </Pill>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td>
                    <Pill
                      $tone={tomStatusIntegracao(r.status_integracao)}
                      title={r.erro_integracao || undefined}
                    >
                      {rotuloStatusIntegracao(r.status_integracao)}
                    </Pill>
                  </td>
                  <td>
                    {r.lead ? (
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/crm/leads/${r.lead}`);
                        }}
                        title={r.lead_nome ? `Abrir ${r.lead_nome} no CRM` : "Abrir lead no CRM"}
                      >
                        {r.lead_numero || "Ver lead"}{" "}
                        <FiArrowRight size={12} aria-hidden="true" />
                      </button>
                    ) : (
                      <span className="tiny muted">—</span>
                    )}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn--ghost btn--sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setDetalhe(r);
                      }}
                      title="Ver todas as respostas"
                      aria-label="Ver detalhe da submissao"
                    >
                      <FiEye size={13} aria-hidden="true" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {colunasOcultas > 0 && (
            <p className="tiny muted" style={{ marginTop: 8 }}>
              + {colunasOcultas} campo(s) — abra uma resposta para ver tudo, ou exporte.
            </p>
          )}
          <Paginacao style={{ marginTop: 12 }}>
            <PagInfo>
              {inicio}–{fim} de {total}
            </PagInfo>
            {totalPaginas > 1 && (
              <PagBotoes>
                <PagBtn
                  type="button"
                  onClick={() => setPagina((p) => Math.max(1, p - 1))}
                  disabled={pagina === 1}
                >
                  ‹ Anterior
                </PagBtn>
                <PagInfo aria-live="polite">
                  Página {pagina} de {totalPaginas}
                </PagInfo>
                <PagBtn
                  type="button"
                  onClick={() => setPagina((p) => Math.min(totalPaginas, p + 1))}
                  disabled={pagina === totalPaginas}
                >
                  Próxima ›
                </PagBtn>
              </PagBotoes>
            )}
          </Paginacao>
        </div>
      )}

      {/* Detalhe da submissao — label -> valor de cada campo + metadados */}
      <SlideOverPanel
        open={Boolean(detalhe)}
        onClose={() => setDetalhe(null)}
        title="Resposta"
        subtitle={detalhe ? formatarRecebidaEm(detalhe.created_at) : undefined}
        width="min(95vw, 520px)"
        footer={
          detalhe && podeGerir && !detalhe.anonimizada_em ? (
            <button
              type="button"
              className="btn btn--danger"
              onClick={() => setConfirmandoExclusao(true)}
            >
              <FiTrash2 size={14} aria-hidden="true" /> Excluir resposta
            </button>
          ) : undefined
        }
      >
        {detalhe && (
          <RespostaDetalheList>
            <div className="row">
              <dt>Origem</dt>
              <dd>
                <Pill $tone={tomOrigem(detalhe.origem_submissao)}>
                  {rotuloOrigem(detalhe.origem_submissao)}
                </Pill>
                {detalhe.eh_teste && (
                  <span className="tiny muted" style={{ marginLeft: 8 }}>
                    envio de teste — fora dos KPIs
                  </span>
                )}
              </dd>
            </div>

            <div className="row">
              <dt>CRM</dt>
              <dd>
                <Pill $tone={tomStatusIntegracao(detalhe.status_integracao)}>
                  {rotuloStatusIntegracao(detalhe.status_integracao)}
                </Pill>
                {detalhe.efeito_lead && detalhe.efeito_lead !== "NENHUM" && (
                  <span className="tiny muted" style={{ marginLeft: 8 }}>
                    {rotuloEfeitoLead(detalhe.efeito_lead)}
                  </span>
                )}
                {detalhe.status_integracao === INTEGRACAO_FALHOU && (
                  <div style={{ marginTop: 8 }}>
                    <p className="tiny" style={{ color: "var(--danger-text)", margin: "0 0 8px" }}>
                      {detalhe.erro_integracao || "O CRM recusou o lead."}
                    </p>
                    {podeGerir && !detalhe.anonimizada_em && (
                      <AsyncButton variant="secondary" onClick={reprocessar} silentSuccess>
                        <FiRefreshCw size={13} aria-hidden="true" /> Reprocessar
                      </AsyncButton>
                    )}
                  </div>
                )}
              </dd>
            </div>

            {detalhe.anonimizada_em && (
              <div className="row">
                <dt>LGPD</dt>
                <dd className="muted">
                  Dados da pessoa removidos em {formatarRecebidaEm(detalhe.anonimizada_em)}.
                </dd>
              </div>
            )}

            {detalhe.lead && (
              <div className="row">
                <dt>Lead</dt>
                <dd>
                  <button
                    type="button"
                    className="btn btn--ghost btn--sm"
                    onClick={() => navigate(`/crm/leads/${detalhe.lead}`)}
                  >
                    {detalhe.lead_numero || "Abrir lead"}
                    {detalhe.lead_nome ? ` · ${detalhe.lead_nome}` : ""}{" "}
                    <FiArrowRight size={12} aria-hidden="true" />
                  </button>
                </dd>
              </div>
            )}

            {detalhe.criado_por_nome && (
              <div className="row">
                <dt>Enviada por</dt>
                <dd>{detalhe.criado_por_nome}</dd>
              </div>
            )}

            {/* Campos do formulario (na ordem), resolvendo label -> valor */}
            {camposInput.map((campo) => {
              const valor = detalhe.dados?.[String(campo.id)];
              const exibe = formatarValorResposta(campo, valor);
              const vazio = exibe === "—";
              return (
                <div className="row" key={campo.id}>
                  <dt title={TIPO_CAMPO_LABEL[campo.tipo] || campo.tipo}>
                    {campo.label || campo.tipo}
                  </dt>
                  <dd
                    className={vazio ? "muted" : undefined}
                    title={textoCruResposta(valor) || undefined}
                  >
                    {exibe}
                  </dd>
                </div>
              );
            })}

            {/* Campos que sairam do formulario depois deste envio (FORM-17) */}
            {camposRemovidosDoDetalhe.length > 0 && (
              <div className="row" role="group" aria-label="Campos removidos">
                <dt>Campos removidos</dt>
                <dd>
                  <p className="tiny muted" style={{ margin: "0 0 6px" }}>
                    Saíram do formulário; o valor enviado continua aqui.
                  </p>
                  {camposRemovidosDoDetalhe.map((c) => (
                    <div key={c.campo_id} title={textoCruResposta(c.valor) || undefined}>
                      <strong>{c.label}:</strong> {formatarValorResposta(c, c.valor)}
                    </div>
                  ))}
                </dd>
              </div>
            )}

            {documentosDoDetalhe.length > 0 && (
              <div className="row" role="group" aria-label="Arquivos enviados">
                <dt>Arquivos</dt>
                <dd>
                  <DocumentoGrid documentos={documentosDoDetalhe} minColuna={180} />
                </dd>
              </div>
            )}

            {detalhe.utm && Object.keys(detalhe.utm).length > 0 && (
              <div className="row">
                <dt>UTM</dt>
                <dd>
                  {Object.entries(detalhe.utm)
                    .map(([k, v]) => `${k}=${v}`)
                    .join(" · ")}
                </dd>
              </div>
            )}

            {detalhe.ip && (
              <div className="row">
                <dt>IP</dt>
                <dd className="mono">{detalhe.ip}</dd>
              </div>
            )}

            {detalhe.user_agent && (
              <div className="row">
                <dt>Navegador</dt>
                <dd className="tiny">{detalhe.user_agent}</dd>
              </div>
            )}
          </RespostaDetalheList>
        )}
      </SlideOverPanel>

      <ConfirmDialog
        open={confirmandoExclusao}
        titulo="Excluir esta resposta?"
        descricao="O envio some da lista e das métricas. O lead criado a partir dela continua no CRM. Esta ação não pode ser desfeita."
        confirmarLabel="Excluir resposta"
        tone="danger"
        onConfirmar={excluir}
        onCancelar={() => setConfirmandoExclusao(false)}
      />
    </PageContainer>
  );
}
